import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { downloadCSV } from '../lib/csv.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { AddWorkerModal } from '../components/domain/AddWorkerModal.js';
import { WorkerDetail } from '../components/domain/WorkerDetail.js';
import { BroadcastComposeModal } from '../components/domain/BroadcastComposeModal.js';
import { UploadModal } from '../components/domain/UploadModal.js';
import { ProximityModal } from '../components/domain/ProximityModal.js';
import { CertNotifyModal } from '../components/domain/CertNotifyModal.js';
import { DeployModal } from '../components/domain/DeployModal.js';
import { MoveWorkerModal } from '../components/domain/MoveWorkerModal.js';
import { WaIcon } from '../components/primitives/WaIcon.js';
import { ratingStars, coreBadge, rtwBadge, natBadge, availBadge as availBadgeShared, avEl as avElShared, initials as initialsShared, avatarColor as avatarColorShared } from '../lib/workerBadges.js';
import { RealMap, type RealMapPin, type RealMapLine } from '../components/primitives/RealMap.js';
import { waLink } from '../lib/wa.js';

interface Cert {
  id: string;
  type: string;
  expiry: string | null;
  status: 'valid' | 'expiring' | 'expired';
}

interface Worker {
  id: string;
  name: string;
  role: string;
  roles?: string[];
  status: 'available' | 'deployed' | 'unavailable' | 'dnu';
  postcode: string;
  phone: string;
  email: string | null;
  core: boolean;
  rating: number | null;
  agencyCode: string | null;
  rtwStatus: string | null;
  rtwExpiry?: string | null;
  nationality?: string | null;
  lat: number | null;
  lng: number | null;
  onSiteUntil: string | null;
  photo?: string | null;
  certs: Cert[];
  projects?: string[];
}

interface Project {
  id: string;
  code: string;
  name: string;
  status: string;
  lat?: number | null;
  lng?: number | null;
  postcode?: string | null;
}

interface Deployment {
  id: string;
  workerId: string;
  projectId: string;
  status: string;
  startDate?: string | null;
  duration?: string | null;
}

interface DnaRecord {
  id: string;
  workerId: string;
  projectId: string;
  startDate: string;
}

interface AvailabilitySignal {
  id: string;
  workerId: string;
  signaledAt: string;
}

type WV = 'cards' | 'table' | 'byproject' | 'map';

const initials = initialsShared;
const avatarColor = avatarColorShared;
const avEl = avElShared;
const availBadge = (w: Worker, signals: AvailabilitySignal[]) => availBadgeShared(w, signals);

function stBdg(status: string): JSX.Element {
  const label = status === 'dnu' ? 'DNU' : status;
  return <span className={`badge b-${status}`}>{label}</span>;
}

function certStatusSummary(w: Worker): 'ok' | 'warn' | 'alert' {
  if (w.certs.some((c) => c.status === 'expired')) return 'alert';
  if (w.certs.some((c) => c.status === 'expiring')) return 'warn';
  return 'ok';
}

function chp(code: string, idx?: number): JSX.Element {
  return <span key={`${code}-${idx ?? 0}`} className="pill" style={{ margin: 1 }}>{code}</span>;
}

function CardActionButtons({ w, certStatus, onProx, onNotify }: { w: Worker; certStatus: 'ok' | 'warn' | 'alert'; onProx: (() => void) | null; onNotify: (() => void) | null }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
      <a className="btn btn-wa btn-xs" href={waLink(w.phone, `Hi ${w.name.split(' ')[0]}, `)} target="_blank" rel="noopener noreferrer" title={`WhatsApp ${w.name}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <WaIcon size={12} />
      </a>
      {onProx ? <button className="btn btn-info btn-xs" onClick={onProx} title="Nearest projects">📍</button> : null}
      {certStatus !== 'ok' && onNotify ? (
        <button className="btn btn-warn btn-xs" onClick={onNotify} title="Notify cert expiry">🔔</button>
      ) : null}
    </div>
  );
}

export function Workers() {
  const [searchParams] = useSearchParams();
  const { canBroadcast, canImport, canExport, canEditWorkers: canAddWorker, isSupervisor, canDeploy, isSiteUser } = useRolePerms();
  const [currentWV, setCurrentWV] = useState<WV>('cards');
  const [fRole, setFRole] = useState('');
  const [fStatus, setFStatus] = useState(searchParams.get('status') ?? '');
  const [fCerts, setFCerts] = useState('');
  const [fCore, setFCore] = useState('');
  const [fRating, setFRating] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deployCtx, setDeployCtx] = useState<{ workerId: string; workerName: string; workerPhone: string; projectId: string; projectName: string } | null>(null);
  const [moveCtx, setMoveCtx] = useState<{ deploymentId: string; workerId: string; workerName: string; workerPhone: string; currentProjectName: string; preselectTargetId?: string } | null>(null);
  const [dragOverZone, setDragOverZone] = useState<string | null>(null);
  const [mobileDrag, setMobileDrag] = useState<{ workerId: string; workerName: string; workerPhone: string; x: number; y: number; panelOpen: boolean } | null>(null);
  const mobileDragPanelOpen = mobileDrag?.panelOpen ?? false;
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [proxFor, setProxFor] = useState<{ id: string; name: string; phone: string; postcode: string } | null>(null);
  const [notifyFor, setNotifyFor] = useState<string | null>(null);
  const [hoverProj, setHoverProj] = useState<{ id: string; x: number; y: number } | null>(null);
  const [hoverWorker, setHoverWorker] = useState<{ id: string; x: number; y: number } | null>(null);
  const [mapProjFocus, setMapProjFocus] = useState<string | null>(null);
  const wmapContRef = useRef<HTMLDivElement | null>(null);

  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data),
  });
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/projects').then((r) => r.data),
  });
  const { data: deployments = [] } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<Deployment[]>('/deployments').then((r) => r.data),
  });
  const { data: dnaRecords = [] } = useQuery({
    queryKey: ['dna-records'],
    queryFn: () => api.get<DnaRecord[]>('/dna-records').then((r) => r.data),
  });
  const qc = useQueryClient();
  const undeployFromProject = useMutation({
    mutationFn: ({ deploymentId }: { deploymentId: string; workerName: string; projectCode: string }) => api.patch(`/deployments/${deploymentId}`, { status: 'cancelled' }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      toast.info('Worker removed from project', `${vars.workerName} ← ${vars.projectCode}`);
    },
    onError: () => toast.error('Remove failed', 'Try again'),
  });
  const { data: availSignals = [] } = useQuery({
    queryKey: ['availability-signals'],
    queryFn: () => api.get<AvailabilitySignal[]>('/availability-signals').then((r) => r.data),
  });
  const dnaCountByWorker = useMemo(() => {
    const m: Record<string, number> = {};
    for (const d of dnaRecords) m[d.workerId] = (m[d.workerId] ?? 0) + 1;
    return m;
  }, [dnaRecords]);

  // Merge worker.projects from active deployments
  const workersWithProjects = useMemo(() => workers.map((w) => ({
    ...w,
    projects: deployments.filter((d) => d.workerId === w.id && d.status === 'active').map((d) => {
      const p = projects.find((x) => x.id === d.projectId);
      return p?.code ?? d.projectId;
    }),
  })), [workers, deployments, projects]);

  const roleOptions = useMemo(() => [...new Set(workers.map((w) => w.role).filter(Boolean))].sort(), [workers]);

  const filtered = useMemo(() => {
    return workersWithProjects.filter((w) => {
      if (fRole && w.role !== fRole) return false;
      if (fStatus && w.status !== fStatus) return false;
      if (fCerts) {
        const cs = certStatusSummary(w);
        if (fCerts === 'ok' && cs !== 'ok') return false;
        if (fCerts === 'warn' && cs !== 'warn') return false;
        if (fCerts === 'alert' && cs !== 'alert') return false;
      }
      if (fCore === 'yes' && !w.core) return false;
      if (fCore === 'no' && w.core) return false;
      if (fRating !== '') {
        const r = w.rating ?? 0;
        if (fRating === '0') { if (r > 0) return false; }
        else { const min = parseInt(fRating, 10); if (r < min) return false; }
      }
      return true;
    });
  }, [workersWithProjects, fRole, fStatus, fCerts, fCore, fRating]);

  const [tableSearch, setTableSearch] = useState('');
  const [deployPanelSearch, setDeployPanelSearch] = useState('');
  const [byprojSearch, setByprojSearch] = useState('');
  const [tblSort, setTblSort] = useState<{ col: 'name' | 'role' | 'status' | 'postcode' | 'phone' | 'certs'; dir: 'asc' | 'desc' } | null>(null);

  useEffect(() => {
    const sp = searchParams.get('status');
    if (sp) setFStatus(sp);
    const focusId = searchParams.get('focus') ?? searchParams.get('id') ?? searchParams.get('wid');
    if (focusId) setDetailId(focusId);
  }, [searchParams]);

  useResetSub(() => {
    setFRole(''); setFStatus(''); setFCerts(''); setFCore(''); setFRating('');
    setTableSearch(''); setDeployPanelSearch(''); setByprojSearch(''); setTblSort(null);
  });

  // Mobile touch-drag: tracks the finger across the page, slides in deploy panel
  // when finger nears the right edge, drops on the .pdzone under the finger.
  useEffect(() => {
    if (!mobileDrag) return;
    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault(); // stop page scroll while dragging
      setMobileDrag((p) => {
        if (!p) return p;
        const panelOpen = p.panelOpen || t.clientX > window.innerWidth - 60;
        return { ...p, x: t.clientX, y: t.clientY, panelOpen };
      });
    };
    const onEnd = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      const drag = mobileDrag;
      setMobileDrag(null);
      if (!t || !drag) return;
      // Hide ghost so elementFromPoint sees what's underneath
      const ghost = document.getElementById('mob-drag-ghost');
      if (ghost) ghost.style.display = 'none';
      const el = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
      if (ghost) ghost.style.display = '';
      if (!el) return;
      const zone = el.closest<HTMLElement>('.pdzone');
      if (!zone || !zone.id.startsWith('pdz-')) return;
      const projectId = zone.id.slice(4);
      const project = projects.find((pp) => pp.id === projectId);
      if (!project) return;
      setDeployCtx({ workerId: drag.workerId, workerName: drag.workerName, workerPhone: drag.workerPhone, projectId: project.id, projectName: project.name });
    };
    const onCancel = () => setMobileDrag(null);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onCancel);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
    };
  }, [mobileDrag, projects]);

  // Highlight pdzone under the finger as the user drags
  useEffect(() => {
    if (!mobileDrag) { setDragOverZone(null); return; }
    const ghost = document.getElementById('mob-drag-ghost');
    if (ghost) ghost.style.display = 'none';
    const el = document.elementFromPoint(mobileDrag.x, mobileDrag.y) as HTMLElement | null;
    if (ghost) ghost.style.display = '';
    const zone = el?.closest<HTMLElement>('.pdzone');
    if (zone && zone.id.startsWith('pdz-')) setDragOverZone(zone.id.slice(4));
    else setDragOverZone(null);
  }, [mobileDrag]);

  // Auto-scroll the project drop-zones list when the finger hovers near
  // its top or bottom edge — so users can reach projects below the fold.
  useEffect(() => {
    if (!mobileDrag?.panelOpen) return;
    const list = document.getElementById('pdzones');
    if (!list) return;
    const tick = () => {
      const r = list.getBoundingClientRect();
      const y = mobileDrag.y;
      const edge = 70; // px hot-zone at each end
      if (y < r.top + edge) list.scrollBy({ top: -12, behavior: 'auto' });
      else if (y > r.bottom - edge) list.scrollBy({ top: 12, behavior: 'auto' });
    };
    const id = window.setInterval(tick, 16);
    return () => window.clearInterval(id);
  }, [mobileDrag]);

  const liveProjects = projects.filter((p) => p.status === 'live');

  return (
    <>
      <div id="work-subnav" style={{ margin: '-16px -16px 0' }}>
        {(['cards', 'table', 'map', 'byproject'] as const).map((v) => (
          <div
            key={v}
            className={`snav-btn ${currentWV === v ? 'active' : ''}`}
            data-wv={v}
            onClick={() => setCurrentWV(v)}
          >
            {v === 'cards' ? '⊞ Cards' : v === 'table' ? '⊟ Table' : v === 'map' ? '◎ Map' : '◈ By Project'}
          </div>
        ))}
        <div className="snav-sep"></div>
        <div className="fpill">
          <span>Role:</span>
          <select id="wf-role" value={fRole} onChange={(e) => setFRole(e.target.value)}>
            <option value="">All</option>
            {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="fpill">
          <span>Status:</span>
          <select id="wf-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
            <option value="">All</option>
            <option value="available">Available</option>
            <option value="deployed">Deployed</option>
            <option value="unavailable">Unavailable</option>
            <option value="dnu">DNU (Do Not Use)</option>
          </select>
        </div>
        <div className="fpill">
          <span>Certs:</span>
          <select id="wf-certs" value={fCerts} onChange={(e) => setFCerts(e.target.value)}>
            <option value="">All</option>
            <option value="ok">Valid</option>
            <option value="warn">Expiring</option>
            <option value="alert">Expired</option>
          </select>
        </div>
        <div className="fpill">
          <span>Core:</span>
          <select id="wf-core" value={fCore} onChange={(e) => setFCore(e.target.value)}>
            <option value="">All</option>
            <option value="yes">Core only</option>
            <option value="no">Non-core</option>
          </select>
        </div>
        {isSupervisor ? null : <div className="fpill">
          <span>Rating:</span>
          <select id="wf-rating" value={fRating} onChange={(e) => setFRating(e.target.value)}>
            <option value="">All</option>
            <option value="5">5★ only</option>
            <option value="4">≥ 4★</option>
            <option value="3">≥ 3★</option>
            <option value="2">≥ 2★</option>
            <option value="1">≥ 1★ (rated)</option>
            <option value="0">Unrated</option>
          </select>
        </div>}
        <div className="snav-sep"></div>
        <div style={{ fontSize: 10, color: 'var(--muted)' }} id="wf-count">{filtered.length}/{workers.length}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
          {canBroadcast ? <button className="btn btn-ghost btn-sm" style={{ color: '#25D366', borderColor: 'rgba(37,211,102,.35)' }} onClick={() => setBroadcastOpen(true)}>📢 Broadcast</button> : null}
          {canImport ? <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import</button> : null}
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV(
            'workers.csv',
            filtered.map((w) => ({
              ...w,
              status: w.status === 'dnu' ? 'DNU' : w.status,
              agency: w.agencyCode ?? '',
              certSummary: certStatusSummary(w),
              projectCodes: (w.projects ?? []).join('; '),
            })) as Array<Record<string, unknown>>,
            [
              { key: 'name', label: 'Name' },
              { key: 'role', label: 'Role' },
              { key: 'status', label: 'Status' },
              { key: 'agency', label: 'Agency' },
              { key: 'phone', label: 'Phone' },
              { key: 'email', label: 'Email' },
              { key: 'postcode', label: 'Postcode' },
              { key: 'certSummary', label: 'Certs' },
              { key: 'projectCodes', label: 'Projects' },
            ]
          )}>⬇ Export</button> : null}
          {canAddWorker ? <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ New Worker</button> : null}
        </div>
      </div>

      <div className="page" id="page-workers" style={{ margin: '0 -16px -16px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div id="wv-cards" style={{ flex: 1, overflow: 'hidden', display: currentWV === 'cards' ? 'flex' : 'none', flexDirection: 'row' }}>
          <div id="wcard-grid">
            {filtered.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 12, padding: 20 }}>No workers match filters.</div>
            ) : filtered.map((w) => {
              const cs = certStatusSummary(w);
              const expN = w.certs.filter((c) => c.status === 'expired').length;
              const dnaC = dnaCountByWorker[w.id] ?? 0;
              return (
                <div
                  key={w.id}
                  className="wcard"
                  id={`wcard-${w.id}`}
                  onClick={() => { if (!mobileDrag) setDetailId(w.id); }}
                  draggable={canDeploy}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/worker-id', w.id);
                    e.dataTransfer.setData('text/worker-name', w.name);
                    e.dataTransfer.setData('text/worker-phone', w.phone);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onTouchStart={(e) => {
                    const t = e.touches[0];
                    setMobileDrag({ workerId: w.id, workerName: w.name, workerPhone: w.phone, x: t.clientX, y: t.clientY, panelOpen: false });
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 9 }}>
                    {avEl(w, 34, 12)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 1, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                        <span>{w.name}</span>
                        {coreBadge(w)}
                        {ratingStars(w, 11)}
                        {rtwBadge(w)}
                        {availBadge(w, availSignals)}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                        {w.role}
                        {w.agencyCode ? <span className={`mx-ag mx-ag-${w.agencyCode}`} style={{ fontSize: 8, verticalAlign: 'middle', marginLeft: 3 }}>{w.agencyCode}</span> : null}
                      </div>
                      <div style={{ marginTop: 3, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                        {stBdg(w.status)}
                        {w.onSiteUntil && w.status !== 'available' ? (
                          <span style={{ fontSize: 9, color: 'var(--muted)' }}>until {w.onSiteUntil.slice(0, 10)}</span>
                        ) : null}
                        {natBadge(w)}
                      </div>
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/${encodeURIComponent(w.postcode)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, textDecoration: 'none' }}
                    >📍{w.postcode}</a>
                  </div>
                  {w.phone ? (
                    <div style={{ marginBottom: 6 }}>
                      <a href={`tel:${w.phone}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 11, color: 'var(--info)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={`Call ${w.name}`}>
                        📞 {w.phone}
                      </a>
                    </div>
                  ) : null}
                  <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 7 }}>
                    {w.certs.map((c) => (
                      <span key={c.id} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: c.status === 'valid' ? 'rgba(34,197,94,.12)' : c.status === 'expiring' ? 'rgba(245,158,11,.12)' : 'rgba(239,68,68,.12)', color: c.status === 'valid' ? '#22C55E' : c.status === 'expiring' ? '#F59E0B' : '#EF4444' }}>
                        {c.type}
                      </span>
                    ))}
                  </div>
                  {cs === 'alert' ? (
                    <div style={{ fontSize: 10, color: 'var(--danger)', marginBottom: 6 }}>⚠ {expN} expired</div>
                  ) : cs === 'warn' ? (
                    <div style={{ fontSize: 10, color: 'var(--warn)', marginBottom: 6 }}>⏱ expiring soon</div>
                  ) : null}
                  {dnaC > 0 ? (
                    <div style={{ fontSize: 10, color: '#ef4444', marginBottom: 6, fontWeight: 600 }}>⚠ {dnaC} DNA record{dnaC !== 1 ? 's' : ''}</div>
                  ) : null}
                  {w.projects && w.projects.length ? (
                    <div style={{ marginBottom: 7 }}>{w.projects.map((code, i) => <span key={`${w.id}-${code}-${i}`} className="pill" style={{ margin: 1 }}>{code}</span>)}</div>
                  ) : null}
                  <CardActionButtons
                    w={w}
                    certStatus={cs}
                    onProx={isSiteUser ? null : () => setProxFor({ id: w.id, name: w.name, phone: w.phone, postcode: w.postcode })}
                    onNotify={canBroadcast ? () => setNotifyFor(w.id) : null}
                  />
                </div>
              );
            })}
          </div>
          {!canDeploy ? null : (
          <div
            id="deploy-panel"
            className={isMobileViewport ? 'mob-overlay' : ''}
            style={isMobileViewport ? { right: mobileDragPanelOpen ? 0 : '-100%' } : undefined}
          >
            <div className="dp-hdr">
              <h3>🎯 Deploy to Project</h3>
              <p>Drag &amp; drop worker cards here</p>
            </div>
            <div style={{ padding: '8px 9px 0' }}>
              <div className="dp-search" style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: '5px 9px', marginBottom: 9 }}>
                <span style={{ color: 'var(--faint)', fontSize: 12 }}>⌕</span>
                <input id="deploy-panel-search" placeholder="Search projects..." value={deployPanelSearch} onChange={(e) => setDeployPanelSearch(e.target.value)} style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 11, flex: 1 }} />
              </div>
            </div>
            <div className="pdzones" id="pdzones">
              {liveProjects.filter((p) => {
                const s = deployPanelSearch.toLowerCase().trim();
                return !s || p.code.toLowerCase().includes(s) || p.name.toLowerCase().includes(s);
              }).map((p) => {
                const dep = workersWithProjects.filter((w) => w.projects!.includes(p.code));
                const isOver = dragOverZone === p.id;
                return (
                  <div
                    key={p.id}
                    className={`pdzone${isOver ? ' drag-target' : ''}`}
                    id={`pdz-${p.id}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                      if (dragOverZone !== p.id) setDragOverZone(p.id);
                    }}
                    onDragLeave={(e) => {
                      if (e.currentTarget === e.target) setDragOverZone(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverZone(null);
                      const workerId = e.dataTransfer.getData('text/worker-id');
                      if (!workerId) return;
                      const workerName = e.dataTransfer.getData('text/worker-name');
                      const workerPhone = e.dataTransfer.getData('text/worker-phone');
                      setDeployCtx({ workerId, workerName, workerPhone, projectId: p.id, projectName: p.name });
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                      <div>
                        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, color: 'var(--accent)' }}>{p.code}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.name}</div>
                      </div>
                      <span className="badge b-live">live</span>
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 4 }}>{dep.length} worker{dep.length !== 1 ? 's' : ''}</div>
                    {dep.map((w) => {
                      const wDep = deployments.find((d) => d.workerId === w.id && d.projectId === p.id && d.status === 'active');
                      return (
                        <div key={w.id} className="dw-row" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 0', borderTop: '1px solid rgba(30,42,64,.4)' }}>
                          {avEl(w, 18, 8)}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 500 }}>{w.name}</div>
                            <div style={{ fontSize: 9, color: 'var(--muted)' }}>{w.role}</div>
                          </div>
                          {wDep ? (
                            <button
                              className="dw-rm"
                              hidden={!canDeploy}
                              title={`Remove ${w.name} from ${p.code}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(`Remove ${w.name} from ${p.code}?`)) {
                                  undeployFromProject.mutate({ deploymentId: wDep.id, workerName: w.name, projectCode: p.code });
                                }
                              }}
                            >×</button>
                          ) : null}
                        </div>
                      );
                    })}
                    <div className="drop-hint">Drop worker here</div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
          {mobileDrag ? (
            <div id="mob-drag-ghost" style={{ left: mobileDrag.x, top: mobileDrag.y }}>
              {mobileDrag.workerName}
            </div>
          ) : null}
        </div>

        <div id="wv-table" style={{ flex: 1, display: currentWV === 'table' ? 'flex' : 'none', overflow: 'hidden' }}>
          <div id="wtable-scroll" style={{ flex: 1, overflow: 'auto' }}>
            <div className="tbl-search" style={{ margin: '14px 14px 0', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px' }}>
              <span style={{ color: 'var(--faint)' }}>⌕</span>
              <input placeholder="Search workers…" value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 12, flex: 1 }} />
            </div>
            <table style={{ marginTop: 0, width: '100%' }}>
              <thead id="wtbl-head">
                <tr>
                  <th onClick={() => setTblSort({ col: 'name', dir: tblSort?.col === 'name' && tblSort.dir === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer', userSelect: 'none' }}>Name {tblSort?.col === 'name' ? (tblSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th onClick={() => setTblSort({ col: 'role', dir: tblSort?.col === 'role' && tblSort.dir === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer', userSelect: 'none' }}>Role {tblSort?.col === 'role' ? (tblSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th>Agency</th>
                  <th onClick={() => setTblSort({ col: 'status', dir: tblSort?.col === 'status' && tblSort.dir === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer', userSelect: 'none' }}>Status {tblSort?.col === 'status' ? (tblSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th onClick={() => setTblSort({ col: 'postcode', dir: tblSort?.col === 'postcode' && tblSort.dir === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer', userSelect: 'none' }}>Postcode {tblSort?.col === 'postcode' ? (tblSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th onClick={() => setTblSort({ col: 'phone', dir: tblSort?.col === 'phone' && tblSort.dir === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer', userSelect: 'none' }}>Phone {tblSort?.col === 'phone' ? (tblSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th onClick={() => setTblSort({ col: 'certs', dir: tblSort?.col === 'certs' && tblSort.dir === 'asc' ? 'desc' : 'asc' })} style={{ cursor: 'pointer', userSelect: 'none' }}>Certs {tblSort?.col === 'certs' ? (tblSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th>Projects</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody id="wtbl-body">
                {(() => {
                  let r = filtered.filter((w) => !tableSearch || [w.name, w.role, w.status, w.postcode, w.phone, w.email].filter(Boolean).join(' ').toLowerCase().includes(tableSearch.toLowerCase()));
                  if (tblSort) {
                    const { col, dir } = tblSort;
                    r = [...r].sort((a, b) => {
                      const av = col === 'certs' ? a.certs.length : (a as any)[col] ?? '';
                      const bv = col === 'certs' ? b.certs.length : (b as any)[col] ?? '';
                      if (av < bv) return dir === 'asc' ? -1 : 1;
                      if (av > bv) return dir === 'asc' ? 1 : -1;
                      return 0;
                    });
                  }
                  return r;
                })()
                  .map((w) => {
                    const cs = certStatusSummary(w);
                    const ci = cs === 'alert' ? <span style={{ color: 'var(--danger)' }}>⚠</span> : cs === 'warn' ? <span style={{ color: 'var(--warn)' }}>⏱</span> : <span style={{ color: 'var(--live)' }}>✓</span>;
                    const wDna = dnaCountByWorker[w.id] ?? 0;
                    return (
                      <tr key={w.id} onClick={() => setDetailId(w.id)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                            {avEl(w, 24, 9)}
                            <span style={{ fontWeight: 500 }}>{w.name}</span>
                            {coreBadge(w, true)}
                            {ratingStars(w, 10)}
                            {rtwBadge(w)}
                            {availBadge(w, availSignals)}
                            {natBadge(w)}
                          </div>
                        </td>
                        <td><span style={{ fontSize: 10, color: 'var(--purple)' }}>{Array.from(new Set([w.role, ...(w.roles ?? [])].filter(Boolean))).join(', ')}</span></td>
                        <td>{w.agencyCode ? <span className={`mx-ag mx-ag-${w.agencyCode}`} style={{ fontSize: 9 }}>{w.agencyCode}</span> : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                        <td>{stBdg(w.status)}</td>
                        <td>
                          <a href={`https://www.google.com/maps/search/${encodeURIComponent(w.postcode)}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none', fontSize: 10 }}>📍{w.postcode}</a>
                        </td>
                        <td style={{ color: 'var(--muted)' }}>{w.phone}</td>
                        <td>
                          {ci} {w.certs.length}
                          {wDna > 0 ? <span style={{ color: '#ef4444', fontSize: 9, marginLeft: 4 }} title={`${wDna} DNA on record`}>⚠{wDna} DNA</span> : null}
                        </td>
                        <td>{w.projects && w.projects.length ? w.projects.map((code, i) => <span key={`${w.id}-${code}-${i}`} className="pill" style={{ margin: 1 }}>{code}</span>) : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <CardActionButtons
                            w={w}
                            certStatus={cs}
                            onProx={isSiteUser ? null : () => setProxFor({ id: w.id, name: w.name, phone: w.phone, postcode: w.postcode })}
                            onNotify={canBroadcast ? () => setNotifyFor(w.id) : null}
                          />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        <div id="wv-byproject" style={{ flex: 1, display: currentWV === 'byproject' ? 'flex' : 'none', overflow: 'hidden', flexDirection: 'column' }}>
          {(() => {
            const s = byprojSearch.toLowerCase().trim();
            let totalW = 0;
            let totalP = 0;
            for (const p of projects) {
              const allDep = workersWithProjects.filter((w) => w.projects!.includes(p.code));
              const projHay = `${p.code} ${p.name} ${(p as any).postcode ?? ''} ${(p as any).customer ?? ''} ${(p as any).manager ?? ''}`.toLowerCase();
              const pMatch = !s || projHay.includes(s);
              const filtW = pMatch ? allDep : allDep.filter((w) => [w.name, w.role, w.status, w.postcode, w.phone, w.email].filter(Boolean).join(' ').toLowerCase().includes(s));
              if (filtW.length === 0) continue;
              totalP++;
              totalW += filtW.length;
            }
            return (
              <div style={{ padding: '14px 14px 0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="tbl-search" style={{ flex: 1, minWidth: 200, margin: 0, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px' }}>
                  <span style={{ color: 'var(--faint)' }}>⌕</span>
                  <input id="byproj-search" placeholder="Search worker name, role, project, postcode…" value={byprojSearch} onChange={(e) => setByprojSearch(e.target.value)} style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 12, flex: 1 }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }} id="byproj-count">
                  {totalW} worker{totalW !== 1 ? 's' : ''} across {totalP} project{totalP !== 1 ? 's' : ''}
                </div>
              </div>
            );
          })()}
          <div style={{ flex: 1, overflowY: 'auto', padding: 14 }} id="byproj-content">
            {[...projects].sort((a, b) => a.code.localeCompare(b.code)).map((p) => {
              const s = byprojSearch.toLowerCase().trim();
              const allDep = workersWithProjects.filter((w) => w.projects!.includes(p.code));
              let filtW = allDep;
              const projHay = `${p.code} ${p.name} ${(p as any).postcode ?? ''} ${(p as any).customer ?? ''} ${(p as any).manager ?? ''}`.toLowerCase();
              const pMatch = !s || projHay.includes(s);
              if (s && !pMatch) {
                filtW = allDep.filter((w) => [w.name, w.role, w.status, w.postcode, w.phone, w.email].filter(Boolean).join(' ').toLowerCase().includes(s));
              }
              if (!filtW.length) return null;
              const isLive = p.status === 'live';
              const bc = isLive ? 'rgba(34,197,94,.25)' : 'var(--border)';
              const hbg = isLive ? 'rgba(34,197,94,.06)' : 'rgba(22,29,46,.5)';
              const actDeps = deployments.filter((d) => d.projectId === p.id && d.status === 'active').length;
              return (
                <div key={p.id} style={{ marginBottom: 18, border: `1px solid ${bc}`, borderRadius: 12, overflow: 'hidden', background: 'var(--card)' }}>
                  <div style={{ background: hbg, padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: isLive ? 'var(--live)' : 'var(--muted)', flexShrink: 0 }}></span>
                    <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 13, color: 'var(--accent)', flexShrink: 0 }}>{p.code}</span>
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    {(p as any).postcode ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>📍 {(p as any).postcode}</span> : null}
                    {(p as any).customerRef?.name ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>{(p as any).customerRef.name}</span> : null}
                    {(p as any).manager ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>⚒ {(p as any).manager}</span> : null}
                    <span className={`badge b-${p.status}`}>{p.status}</span>
                    <span style={{ background: 'var(--accent-dim)', border: '1px solid rgba(232,164,39,.2)', borderRadius: 20, padding: '2px 9px', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)' }}>{filtW.length} worker{filtW.length !== 1 ? 's' : ''}</span>
                    {actDeps > 0 ? <span style={{ background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 20, padding: '2px 9px', fontSize: 10, color: 'var(--live)' }}>{actDeps} active</span> : null}
                    {(p as any).endDate ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>Ends {String((p as any).endDate).slice(0, 10)}</span> : null}
                  </div>
                  <div style={{ display: 'flex', minWidth: 0 }}>
                    <div style={{ flex: 1, overflowX: 'auto' }}>
                      <table style={{ margin: 0 }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '9px 14px' }}>Worker</th>
                            <th>Status</th>
                            <th>Roles</th>
                            <th>Postcode</th>
                            <th>Phone</th>
                            <th>Certs</th>
                            <th>On Site Since</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtW.map((w) => {
                            const cs = certStatusSummary(w);
                            const ci = cs === 'alert' ? <span style={{ color: 'var(--danger)' }}>⚠</span> : cs === 'warn' ? <span style={{ color: 'var(--warn)' }}>⏱</span> : <span style={{ color: 'var(--live)' }}>✓</span>;
                            const dep = deployments.find((d) => d.workerId === w.id && d.projectId === p.id && d.status === 'active');
                            const wDna = dnaCountByWorker[w.id] ?? 0;
                            return (
                              <tr key={w.id} onClick={() => setDetailId(w.id)} style={{ cursor: 'pointer' }}>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                                    {avEl(w, 24, 9)}
                                    <span style={{ fontWeight: 500 }}>{w.name}</span>
                                    {coreBadge(w, true)}
                                    {ratingStars(w, 10)}
                                    {rtwBadge(w)}
                                    {availBadge(w, availSignals)}
                                    {natBadge(w)}
                                  </div>
                                </td>
                                <td>{stBdg(w.status)}</td>
                                <td><span style={{ fontSize: 10, color: 'var(--purple)' }}>{Array.from(new Set([w.role, ...(w.roles ?? [])].filter(Boolean))).join(', ')}</span></td>
                                <td>
                                  <a href={`https://www.google.com/maps/search/${encodeURIComponent(w.postcode)}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none', fontSize: 10 }}>📍{w.postcode}</a>
                                </td>
                                <td style={{ color: 'var(--muted)', fontSize: 11 }}>{w.phone}</td>
                                <td>
                                  {ci} {w.certs.length}
                                  {wDna > 0 ? <span style={{ color: '#ef4444', fontSize: 9, marginLeft: 4 }} title={`${wDna} DNA on record`}>⚠{wDna} DNA</span> : null}
                                </td>
                                <td>
                                  {dep?.startDate ? (
                                    <>
                                      <div style={{ fontSize: 11 }}>{String(dep.startDate).slice(0, 10)}</div>
                                      {dep.duration ? <div style={{ fontSize: 9, color: 'var(--faint)' }}>{dep.duration}</div> : null}
                                    </>
                                  ) : <span style={{ color: 'var(--faint)', fontSize: 11 }}>—</span>}
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <div style={{ display: 'flex', gap: 3 }}>
                                    <a className="btn btn-wa btn-xs" href={waLink(w.phone, `Hi ${w.name.split(' ')[0]}, re: ${p.code} — ${p.name}. `)} target="_blank" rel="noopener noreferrer" title={`WhatsApp ${w.name}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                      <WaIcon size={12} />
                                    </a>
                                    {cs !== 'ok' ? (
                                      <button className="btn btn-warn btn-xs" onClick={() => setNotifyFor(w.id)} title="Notify cert expiry">🔔</button>
                                    ) : null}
                                    <button
                                      className="btn btn-info btn-xs"
                                      title="Move to another project"
                                      onClick={() => dep ? setMoveCtx({ deploymentId: dep.id ?? '', workerId: w.id, workerName: w.name, workerPhone: w.phone, currentProjectName: p.name }) : null}
                                      disabled={!dep?.id}
                                    >↔</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ width: 220, minWidth: 220, borderLeft: '1px solid var(--border)', background: 'var(--surface)', padding: 12, flexShrink: 0 }}>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 9 }}>↔ Move Worker</div>
                      <div style={{ fontSize: 10, color: 'var(--faint)', marginBottom: 9 }}>Select a worker from the table, then click ↔ to deploy them to another project.</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 5 }}>Quick deploy to:</div>
                      {(() => {
                        const otherLive = projects.filter((lp) => lp.status === 'live' && lp.id !== p.id);
                        if (!otherLive.length) return <div style={{ fontSize: 10, color: 'var(--faint)' }}>No other live projects</div>;
                        return otherLive.map((lp) => (
                          <div key={lp.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: 8, marginBottom: 6 }}>
                            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)' }}>{lp.code}</div>
                            <div style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 5 }}>{lp.name}</div>
                            {lp.postcode ? <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 5 }}>📍 {lp.postcode}</div> : null}
                            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                              {filtW.map((fw) => {
                                const fwDep = deployments.find((dx) => dx.workerId === fw.id && dx.projectId === p.id && dx.status === 'active');
                                if (!fwDep?.id) return null;
                                return (
                                  <button
                                    key={fw.id}
                                    className="btn btn-ghost btn-xs"
                                    style={{ fontSize: 9 }}
                                    title={`Move ${fw.name} → ${lp.code}`}
                                    onClick={() => setMoveCtx({ deploymentId: fwDep.id, workerId: fw.id, workerName: fw.name, workerPhone: fw.phone, currentProjectName: p.name, preselectTargetId: lp.id })}
                                  >
                                    {fw.name.split(' ')[0]}→{lp.code}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div id="wmap-view" style={{ flex: 1, display: currentWV === 'map' ? 'flex' : 'none', flexDirection: 'row', overflow: 'hidden' }}>
          <div id="wmap-cont" ref={wmapContRef}>
            {currentWV === 'map' ? (() => {
              const workerPins = filtered.filter((w) => w.lat != null && w.lng != null);
              const projPins = projects.filter((p: any) => p.lat != null && p.lng != null);
              const avc = (name: string): string => {
                let h = 0;
                for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
                return `hsl(${h % 360}, 60%, 55%)`;
              };
              const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
              const realLines: RealMapLine[] = [];
              workerPins.forEach((w) => (w.projects ?? []).forEach((code) => {
                const p = projPins.find((x: any) => x.code === code || x.id === code);
                if (!p) return;
                realLines.push({
                  id: `${w.id}-${p.id}`,
                  from: { lat: w.lat!, lng: w.lng! },
                  to: { lat: p.lat!, lng: p.lng! },
                  color: 'rgba(232,164,39,.15)',
                  weight: 1,
                  dashArray: '4,4',
                });
              }));
              const realPins: RealMapPin[] = [
                ...projPins.map((p: any) => {
                  const col = p.status === 'live' ? 'var(--live)' : 'var(--muted)';
                  return {
                    id: `p-${p.id}`,
                    lat: p.lat!,
                    lng: p.lng!,
                    iconHtml: `<div style="display:flex;flex-direction:column;align-items:center"><div style="position:relative"><div class="wmap-pdot ${p.status}" style="background:${col}"></div><div style="position:absolute;top:-15px;left:50%;transform:translateX(-50%);white-space:nowrap;font-size:8px;font-family:'Syne', sans-serif;font-weight:700;color:var(--accent);background:rgba(11,15,26,.9);padding:1px 3px;border-radius:2px">${esc(p.code ?? '')}</div></div><div class="wmap-pstem" style="background:${col}"></div></div>`,
                    iconSize: [44, 42] as [number, number],
                    iconAnchor: [22, 38] as [number, number],
                    zIndexOffset: 100,
                  };
                }),
                ...workerPins.map((w) => {
                  const c = avc(w.name);
                  const inner = w.photo
                    ? `<img src="${esc(w.photo)}" alt="${esc(w.name)}" loading="lazy" style="width:100%;height:100%;object-fit:cover" />`
                    : esc(initials(w.name));
                  return {
                    id: `w-${w.id}`,
                    lat: w.lat!,
                    lng: w.lng!,
                    iconHtml: `<div style="display:flex;flex-direction:column;align-items:center"><div class="wmap-pav" style="background:${w.photo ? 'var(--card)' : c};overflow:hidden;padding:0">${inner}</div><div class="wmap-pstem" style="background:${c}"></div></div>`,
                    iconSize: [32, 36] as [number, number],
                    iconAnchor: [16, 35] as [number, number],
                    zIndexOffset: 200,
                  };
                }),
              ];
              return (
                <RealMap
                  pins={realPins}
                  lines={realLines}
                  height="100%"
                  tileTheme="light"
                  onPinEnter={(id, cp) => {
                    if (id.startsWith('p-')) setHoverProj({ id: id.slice(2), x: cp.x, y: cp.y });
                    else if (id.startsWith('w-')) setHoverWorker({ id: id.slice(2), x: cp.x, y: cp.y });
                  }}
                  onPinLeave={(id) => {
                    if (id.startsWith('p-')) setHoverProj(null);
                    else if (id.startsWith('w-')) setHoverWorker(null);
                  }}
                  onPinClick={(id) => {
                    if (id.startsWith('p-')) setMapProjFocus(id.slice(2));
                    else if (id.startsWith('w-')) setDetailId(id.slice(2));
                  }}
                >
                  {(() => {
                    const W = wmapContRef.current?.clientWidth ?? 800;
                    if (hoverProj) {
                      const p = projects.find((x) => x.id === hoverProj.id);
                      if (!p) return null;
                      return (
                        <div className="wmap-tt on" style={{ left: Math.min(hoverProj.x + 14, W - 230), top: Math.max(4, hoverProj.y - 80) }}>
                          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, color: 'var(--accent)', marginBottom: 2 }}>{p.code}</div>
                          <div style={{ fontSize: 11, fontWeight: 500 }}>{p.name}</div>
                          {(p as any).address ? <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{(p as any).address}</div> : null}
                        </div>
                      );
                    }
                    if (hoverWorker) {
                      const w = workersWithProjects.find((x) => x.id === hoverWorker.id);
                      if (!w) return null;
                      return (
                        <div className="wmap-tt on" style={{ left: Math.min(hoverWorker.x + 14, W - 230), top: Math.max(4, hoverWorker.y - 80) }}>
                          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, color: 'var(--accent)', marginBottom: 2 }}>{w.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{w.role}{w.agencyCode ? ` · ${w.agencyCode}` : ''} · {w.postcode}</div>
                          <div style={{ marginTop: 4 }}>{stBdg(w.status)}</div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </RealMap>
              );
            })() : null}
            <div id="wmap-legend">
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 7 }}>Legend</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, fontSize: 10 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--accent)', border: '2px solid rgba(255,255,255,.5)' }}></div>Worker
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, fontSize: 10 }}>
                <div className="wmap-pdot live" style={{ width: 11, height: 11 }}></div>Live project
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10 }}>
                <div className="wmap-pdot" style={{ width: 11, height: 11, background: 'var(--muted)' }}></div>Completed
              </div>
            </div>
          </div>
          <div id="wmap-sidebar" className="mob-hide">
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }} id="wmap-sb-title">
              {mapProjFocus ? `Distance to ${(projects.find((x) => x.id === mapProjFocus)?.code) ?? '—'}` : 'All Workers'}
            </div>
            <div style={{ fontSize: 10, color: 'var(--faint)', marginBottom: 8 }}>
              {mapProjFocus ? <button className="btn btn-ghost btn-xs" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setMapProjFocus(null)}>✕ Clear</button> : 'Click a project pin to sort by distance'}
            </div>
            <div id="wmap-wlist">
              {(() => {
                const km = (la1: number, ln1: number, la2: number, ln2: number): number => {
                  const R = 6371;
                  const dLat = (la2 - la1) * Math.PI / 180;
                  const dLng = (ln2 - ln1) * Math.PI / 180;
                  const a = Math.sin(dLat / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
                  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                };
                const focusProj = mapProjFocus ? projects.find((x) => x.id === mapProjFocus) : null;
                const list = filtered.filter((w) => w.lat != null && w.lng != null).map((w) => ({
                  w,
                  dist: focusProj && focusProj.lat != null && focusProj.lng != null ? km(w.lat!, w.lng!, focusProj.lat!, focusProj.lng!) : null,
                }));
                if (focusProj) list.sort((a, b) => (a.dist ?? 0) - (b.dist ?? 0));
                return list.map(({ w, dist }) => (
                  <div key={w.id} className="wmap-wrow" onClick={() => setDetailId(w.id)}>
                    {avEl(w, 26, 10)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <span>{w.name}</span>{coreBadge(w, true)}{ratingStars(w, 9)}{rtwBadge(w)}{availBadge(w, availSignals)}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--muted)' }}>
                        {w.role}{w.agencyCode ? <> · <span style={{ color: 'var(--info)' }}>{w.agencyCode}</span></> : null}
                      </div>
                    </div>
                    {dist != null ? (
                      <span style={{ background: 'var(--accent-dim)', border: '1px solid rgba(232,164,39,.25)', borderRadius: 12, padding: '2px 8px', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)' }}>{dist.toFixed(1)}km</span>
                    ) : (
                      <span style={{ fontSize: 9, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{w.postcode}</span>
                    )}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>
      {addOpen ? <AddWorkerModal onClose={() => setAddOpen(false)} /> : null}
      {detailId ? <WorkerDetail workerId={detailId} onClose={() => setDetailId(null)} /> : null}
      {broadcastOpen ? <BroadcastComposeModal onClose={() => setBroadcastOpen(false)} /> : null}
      {importOpen ? <UploadModal entity="workers" onClose={() => setImportOpen(false)} /> : null}
      {deployCtx ? (
        <DeployModal
          onClose={() => setDeployCtx(null)}
          workerId={deployCtx.workerId}
          workerName={deployCtx.workerName}
          workerPhone={deployCtx.workerPhone}
          projectId={deployCtx.projectId}
          projectName={deployCtx.projectName}
        />
      ) : null}
      {proxFor ? (
        <ProximityModal
          onClose={() => setProxFor(null)}
          workerId={proxFor.id}
          workerName={proxFor.name}
          workerPhone={proxFor.phone}
          workerPostcode={proxFor.postcode}
        />
      ) : null}
      {notifyFor ? <CertNotifyModal onClose={() => setNotifyFor(null)} workerId={notifyFor} /> : null}
      {moveCtx ? (
        <MoveWorkerModal
          onClose={() => setMoveCtx(null)}
          deploymentId={moveCtx.deploymentId}
          workerId={moveCtx.workerId}
          workerName={moveCtx.workerName}
          workerPhone={moveCtx.workerPhone}
          currentProjectName={moveCtx.currentProjectName}
          preselectTargetId={moveCtx.preselectTargetId}
        />
      ) : null}
    </>
  );
}
